import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Request interceptor — inject current access token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// ---------------------------------------------------------------------------
// Response interceptor — auto-refresh access token on 401
// ---------------------------------------------------------------------------
// Behaviour:
//  - On 401 for a regular request: attempt to refresh the access token using
//    the stored refresh_token via POST /auth/refresh, then retry the original
//    request once.
//  - If multiple requests fail with 401 concurrently, only one refresh call
//    is performed; the other requests are queued and resumed afterwards.
//  - If the refresh itself fails, or no refresh_token is available, clear
//    local auth state and redirect to /login (or /portal/login if already
//    in the Resident Portal context).
//  - /auth/login and /auth/refresh are excluded from this flow to avoid
//    infinite loops.
// ---------------------------------------------------------------------------

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean }

let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (err: unknown) => void
}> = []

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((p) => {
    if (error) {
      p.reject(error)
    } else if (token) {
      p.resolve(token)
    }
  })
  failedQueue = []
}

const redirectToLogin = () => {
  localStorage.removeItem('auth_token')
  localStorage.removeItem('refresh_token')
  if (typeof window === 'undefined') return
  const path = window.location.pathname
  // Resident portal context — send back to /portal/login.
  if (path.startsWith('/portal')) {
    if (!path.startsWith('/portal/login') && !path.startsWith('/portal/activate')) {
      window.location.href = '/portal/login'
    }
    return
  }
  // Admin context — /login (avoid loop if already there).
  if (!path.startsWith('/login')) {
    window.location.href = '/login'
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableConfig | undefined
    const status = error.response?.status
    const url = originalRequest?.url || ''

    // Not a 401, or no config to retry with — pass through
    if (status !== 401 || !originalRequest) {
      return Promise.reject(error)
    }

    // Never try to refresh for the refresh/login endpoints themselves
    if (url.includes('/auth/refresh') || url.includes('/auth/login')) {
      // 401 on refresh itself => session is dead
      if (url.includes('/auth/refresh')) {
        redirectToLogin()
      }
      return Promise.reject(error)
    }

    // Already retried once — give up, log out
    if (originalRequest._retry) {
      redirectToLogin()
      return Promise.reject(error)
    }

    // Another request is already refreshing — queue this one
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            resolve(apiClient(originalRequest))
          },
          reject: (err: unknown) => reject(err),
        })
      })
    }

    originalRequest._retry = true
    isRefreshing = true

    try {
      const refreshToken = localStorage.getItem('refresh_token')
      if (!refreshToken) {
        throw new Error('No refresh token available')
      }

      // Use a bare axios call (not apiClient) so the refresh request itself
      // is not intercepted/retried by this same interceptor.
      const response = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        { refresh_token: refreshToken },
        { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
      )

      const newAccess: string = response.data?.access_token
      const newRefresh: string | undefined = response.data?.refresh_token

      if (!newAccess) {
        throw new Error('Refresh response missing access_token')
      }

      localStorage.setItem('auth_token', newAccess)
      if (newRefresh) {
        localStorage.setItem('refresh_token', newRefresh)
      }

      processQueue(null, newAccess)

      originalRequest.headers.Authorization = `Bearer ${newAccess}`
      return apiClient(originalRequest)
    } catch (refreshError) {
      processQueue(refreshError, null)
      redirectToLogin()
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)

export default apiClient
